import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import Spinner from 'modules/common/spinner.component';
import {
  postSurveyQuestionVariation,
  updateSurveyQuestionVariation,
  deleteSurveyQuestionVariation,
  getSurveyQuestions,
  updateClientMapping
} from '../surveys.actions';
import { Clients } from './surveyQuestionConstants';

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Button,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  Box,
  Tooltip,
  Typography,
  IconButton,
  Modal
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faEdit } from '@fortawesome/free-regular-svg-icons';
import { POST_SURVEY_QUESTION_VARIATION_FAILURE, UPDATE_SURVEY_QUESTION_VARIATION_FAILURE } from '../surveys.types';
import SpinnerComponent from 'modules/common/spinner.component';

const RowModal = ({
  open,
  onClose,
  onSave,
  initialValues,
  unmappedClients,
  updateSurveyQuestionVariation,
  showToast,
  getSurveyQuestions,
  taggedItems = []
}) => {
  const [variationText, setVariationText] = useState(
    initialValues.variationInfo.variationText ? initialValues.variationInfo.variationText : ''
  );
  const [taggedItemId, setTaggedItemId] = useState(
    initialValues.variationInfo.taggedItemId ? initialValues.variationInfo.taggedItemId : null
  );
  const questionVariationClientMaps = initialValues.questionVariationClientMaps ? initialValues.questionVariationClientMaps : [];

  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);

    const updateSurveyQuestionVariationResp = await updateSurveyQuestionVariation({
      variationInfo: {
        surveyQuestionVariationId: initialValues.variationInfo.surveyQuestionVariationId,
        surveyQuestionId: initialValues.variationInfo.surveyQuestionId,
        variationText: variationText,
        isDefault: initialValues.variationInfo.isDefault,
        version: initialValues.variationInfo.version,
        statusId: initialValues.variationInfo.statusId,
        originalVersionId: initialValues.variationInfo.originalVersionId,
        createdDate: initialValues.variationInfo.createdDate,
        taggedItemId: taggedItemId,
        addedBy: initialValues.variationInfo.addedBy || 1,
        addedOn: initialValues.variationInfo.addedOn || initialValues.variationInfo.createdDate || new Date().toISOString(),
        modifiedBy: initialValues.variationInfo.modifiedBy || 1,
        modifiedOn: new Date().toISOString()
      },
      questionVariationClientMaps: [...questionVariationClientMaps]
    });

    if (updateSurveyQuestionVariationResp.type === UPDATE_SURVEY_QUESTION_VARIATION_FAILURE) {
      showToast('Failed to update question variation, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } else {
      await getSurveyQuestions();
      showToast('Successfully updated question variation.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      onSave(updateSurveyQuestionVariationResp.response);
    }

    setIsLoading(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          padding: 4,
          boxShadow: 24,
          width: 800
        }}
      >
        <Typography variant="h6" mb={1}>
          Question Variation
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          Write your question here, using <strong>@</strong> as the wildcard.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              label="Question"
              value={variationText}
              onChange={e => setVariationText(e.target.value)}
              multiline
              minRows={3}
              sx={{
                textarea: {
                  fontSize: '1rem',
                  padding: '12px'
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" color="text.secondary" whiteSpace="nowrap">
                <strong>@ =</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="tagged-item-label">Tagged Item</InputLabel>
                <Select
                  labelId="tagged-item-label"
                  id="tagged-item"
                  label="Tagged Item"
                  value={taggedItemId}
                  onChange={e => setTaggedItemId(e.target.value)}
                >
                  {taggedItems.map(option => (
                    <MenuItem key={option.surveyTaggedItemId} value={option.surveyTaggedItemId}>
                      {option.matchValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>

        {!isLoading && (
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={onClose} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        )}

        {isLoading && (
          <Box mt={3} display="flex" justifyContent="flex-end">
            <SpinnerComponent />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const QuestionVariationForm = ({
  formValues = [],
  formFields,
  createNewRowFunc,
  addRowLabel,
  isDisabled = false,
  header,
  headerCheckbox,
  unmappedClients,
  postSurveyQuestionVariation,
  showToast,
  updateSurveyQuestionVariation,
  deleteSurveyQuestionVariation,
  getSurveyQuestions,
  surveyQuestionId,
  updateClientMapping,
  taggedItems = []
}) => {
  const [formValuesState, setFormValuesState] = useState(formValues ? formValues : []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [clientGridOpen, setClientGridOpen] = useState(false);

  const [showAddVariation, setShowAddVariation] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newTaggedItemId, setNewTaggedItemId] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const freshValues = [...formValues];
    setFormValuesState(freshValues);
  }, [formValues]);

  const handleModalOpen = (row = null, index = null) => {
    setEditingRowIndex(index);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setEditingRowIndex(null);
    setModalOpen(false);
  };

  const handleModalSave = async allRows => {
    setFormValuesState(allRows);
    setModalOpen(false);
  };

  const removeFormRow = async variation => {
    setIsLoading(true);

    const deleteVariationResp = await deleteSurveyQuestionVariation(
      variation.variationInfo.surveyQuestionId,
      variation.variationInfo.surveyQuestionVariationId
    );
    if (deleteVariationResp.type === UPDATE_SURVEY_QUESTION_VARIATION_FAILURE) {
      showToast('Failed to delete question variation, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } else {
      showToast('Successfully deleted question variation.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      setFormValuesState([...deleteVariationResp.response]);
    }

    setIsLoading(false);
  };

  const renderHeader = () => {
    if (!header) return null;
    return (
      <Box display="flex" alignItems="center">
        {header.iconPath && (
          <Tooltip title={header.title}>
            <FontAwesomeIcon icon={header.iconPath} size="2x" />
          </Tooltip>
        )}
        <Typography variant="h5" fontWeight="bold" sx={{ ml: 2 }}>
          {header.title}
        </Typography>
        {headerCheckbox && headerCheckbox()}
      </Box>
    );
  };

  const saveNewVariation = async () => {
    setIsLoading(true);

    const newVariationResp = await postSurveyQuestionVariation({
      surveyQuestionId: surveyQuestionId,
      variationText: newQuestion,
      isDefault: false,
      version: 1,
      statusId: '00000000-0000-0000-0000-000000000000',
      originalVersionId: null,
      createdDate: new Date().toISOString(),
      taggedItemId: newTaggedItemId ? newTaggedItemId : 1, // Default to 'N/a' if no tagged item selected
      addedBy: 1, // TODO: Replace with current user
      addedOn: new Date(),
      modifiedBy: 1, // TODO: Replace with current user
      modifiedOn: new Date()
    });

    if (newVariationResp.type === POST_SURVEY_QUESTION_VARIATION_FAILURE) {
      showToast('Failed to add new question variation, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    } else {
      showToast('Successfully added new question variation.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      setFormValuesState([...newVariationResp.response]);
      setNewQuestion('');
      setNewTaggedItemId(null);
      setShowAddVariation(false);
    }

    setIsLoading(false);
  };
  const handleClientVariationToggle = (clientId, variationId) => {
    setFormValuesState(prevState => {
      return prevState.map(variation => {
        const filteredMaps = variation.questionVariationClientMaps.filter(map => map.clientId !== clientId);
        const isCurrentlyAssigned = variation.questionVariationClientMaps.some(map => map.clientId === clientId);
        if (variation.variationInfo.surveyQuestionVariationId === variationId) {
          if (isCurrentlyAssigned) {
            return {
              ...variation,
              questionVariationClientMaps: filteredMaps
            };
          } else {
            return {
              ...variation,
              questionVariationClientMaps: [
                ...filteredMaps,
                {
                  variationId: variationId,
                  clientId: clientId
                }
              ]
            };
          }
        }
        return {
          ...variation,
          questionVariationClientMaps: filteredMaps
        };
      });
    });
  };

  const handleSelectAllClientsForVariation = variationId => {
    setFormValuesState(prevState => {
      return prevState.map(variation => {
        if (variation.variationInfo.surveyQuestionVariationId === variationId) {
          const allClientMappings = unmappedClients.map(client => ({
            variationId: variationId,
            clientId: client.clientRecordId
          }));
          return {
            ...variation,
            questionVariationClientMaps: allClientMappings
          };
        } else {
          return {
            ...variation,
            questionVariationClientMaps: []
          };
        }
      });
    });
  };

  const handleSaveClientVariations = async () => {
    setIsLoading(true);

    try {
      const allClientMappings = formValuesState.flatMap(variation =>
        variation.questionVariationClientMaps.map(mapping => ({
          surveyQuestionVariationClientMapId: mapping.surveyQuestionVariationClientMapId || 0,
          variationId: mapping.variationId,
          clientId: mapping.clientId
        }))
      );

      const updateResp = await updateClientMapping(allClientMappings);

      if (updateResp.type === UPDATE_SURVEY_QUESTION_VARIATION_FAILURE) {
        showToast('Failed to update client assignments, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
        setIsLoading(false);
        return;
      }

      await getSurveyQuestions();
      showToast('Successfully updated client assignments.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      setClientGridOpen(false);
    } catch (error) {
      showToast('Failed to update client assignments, please try again later', TOAST_MESSAGE_SEVERITY_ERROR);
    }

    setIsLoading(false);
  };
  return (
    <Grid item xs={12}>
      <Paper className="card-root" sx={{ p: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          {renderHeader()}
          <Button disabled={isDisabled} onClick={() => setShowAddVariation(true)}>
            {addRowLabel}
          </Button>
        </Grid>
        {formValuesState.map((row, index) => (
          <Box key={index} mb={2}>
            <Grid container alignItems="center" spacing={3}>
              <React.Fragment key={'variationText'}>
                <Grid item xs={6}>
                  <TextField
                    label={'Variation Text'}
                    value={row.variationInfo.variationText}
                    fullWidth
                    disabled
                    inputProps={{
                      style: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label={'Tagged Item'}
                    value={
                      row.variationInfo.taggedItemId && taggedItems
                        ? taggedItems.find(option => option.surveyTaggedItemId === row.variationInfo.taggedItemId)?.matchValue
                        : 'N/A'
                    }
                    fullWidth
                    disabled
                    inputProps={{
                      style: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                </Grid>
              </React.Fragment>
              <Grid item xs="auto">
                <IconButton onClick={() => handleModalOpen(row, index)} disabled={isDisabled}>
                  <FontAwesomeIcon icon={faEdit} />
                </IconButton>
                <IconButton onClick={() => removeFormRow(row)} disabled={isDisabled}>
                  <FontAwesomeIcon icon={faTrashAlt} />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Grid container justifyContent="flex-end" mt={2}>
          <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
            <Button variant="contained" onClick={() => setClientGridOpen(true)} disabled={isDisabled}>
              Client Variation List
            </Button>
          </Box>
        </Grid>
        {formValuesState && formValuesState[editingRowIndex] && (
          <RowModal
            open={modalOpen}
            onClose={handleModalClose}
            onSave={handleModalSave}
            formFields={formFields}
            initialValues={editingRowIndex !== null ? formValuesState[editingRowIndex] : createNewRowFunc()}
            unmappedClients={unmappedClients}
            updateSurveyQuestionVariation={updateSurveyQuestionVariation}
            showToast={showToast}
            getSurveyQuestions={getSurveyQuestions}
            taggedItems={taggedItems}
          />
        )}
      </Paper>
      <Modal open={clientGridOpen} onClose={() => setClientGridOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            padding: 4,
            boxShadow: 24,
            width: 1000,
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h6" mb={2}>
            Client Variation Assignment
          </Typography>

          {/* Header Row */}
          <Box sx={{ mb: 1 }}>
            <Grid container>
              <Grid item xs={3}>
                <Box sx={{ padding: '8px' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Client
                  </Typography>
                </Box>
              </Grid>
              {formValuesState.map((variation, i) => (
                <Grid item xs key={i}>
                  <Box sx={{ padding: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllClientsForVariation(variation.variationInfo.surveyQuestionVariationId)}
                      sx={{ fontSize: '0.65rem', textTransform: 'none', minWidth: 'auto' }}
                    >
                      Select All
                    </Button>
                    <Typography variant="subtitle2" fontWeight="bold" align="center">
                      Variation {i + 1}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Scrollable Client List */}
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            {unmappedClients.map(client => (
              <Grid container key={client.clientRecordId} alignItems="center">
                <Grid item xs={3}>
                  <Box sx={{ padding: '8px' }}>{client.shortName}</Box>
                </Grid>
                {formValuesState.map((variation, i) => {
                  const isAssigned = variation.questionVariationClientMaps.some(c => c.clientId === client.clientRecordId);
                  return (
                    <Grid item xs key={i}>
                      <Box
                        onClick={() =>
                          handleClientVariationToggle(client.clientRecordId, variation.variationInfo.surveyQuestionVariationId)
                        }
                        sx={{
                          height: 40,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        {isAssigned && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ))}
          </Box>

          {/* Footer */}
          {!isLoading && (
            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
              <Button
                onClick={() => {
                  setFormValuesState(formValues ? formValues : []);
                  setClientGridOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveClientVariations}>
                Save
              </Button>
            </Box>
          )}
          {isLoading && (
            <Box display="flex" justifyContent="center" mt={3}>
              <SpinnerComponent />
            </Box>
          )}
        </Box>
      </Modal>
      <Modal open={showAddVariation} onClose={() => setShowAddVariation(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            padding: 4,
            boxShadow: 24,
            width: 800
          }}
        >
          <Typography variant="h6" mb={1}>
            Question Variation
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Write your question here, using <strong>@</strong> as the wildcard.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Question"
                value={newQuestion}
                onChange={e => setNewQuestion(e.target.value)}
                multiline
                minRows={3}
                sx={{
                  textarea: {
                    fontSize: '1rem',
                    padding: '12px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" color="text.secondary" whiteSpace="nowrap">
                  <strong>@ =</strong>
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="tagged-item-label">Tagged Item</InputLabel>
                  <Select
                    labelId="tagged-item-label"
                    id="tagged-item"
                    label="Tagged Item"
                    value={newTaggedItemId}
                    onChange={e => setNewTaggedItemId(e.target.value)}
                  >
                    {taggedItems.map(option => (
                      <MenuItem key={option.surveyTaggedItemId} value={option.surveyTaggedItemId}>
                        {option.matchValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
          {!isLoading && (
            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button onClick={() => setShowAddVariation(false)} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={saveNewVariation}>
                Save
              </Button>
            </Box>
          )}
          {isLoading && (
            <Box mt={3} display="flex" justifyContent="flex-end">
              <SpinnerComponent />
            </Box>
          )}
        </Box>
      </Modal>
    </Grid>
  );
};

const SurveyQuestionSetupCard = props => {
  const { surveyQuestionVariations, surveyQuestionId, isReadOnly, unmappedClients, taggedItems } = props;

  const formFields = [
    {
      name: 'surveyQuestionVariationId',
      type: 'hidden'
    },
    {
      name: 'surveyQuestionId',
      type: 'hidden'
    },
    { name: 'isPrimaryVariation', type: 'checkbox', label: 'Is Primary Variation' },
    { name: 'variationText', type: 'text', label: 'Name' },
    { name: 'variationClientSet', type: 'select', options: Clients, label: 'Clients' }
  ];
  const formLabel = 'variations';
  const addRowLabel = 'Add New Variation';
  const header = { title: 'Question Setup', iconPath: ['fas', 'gears'] };

  const createNewRowFunc = () => {
    return {
      surveyQuestionVariationId: 0,
      surveyQuestionId: surveyQuestionId,
      isPrimaryVariation: false,
      variationText: '',
      variationClientSet: '',
      taggedItemId: null
    };
  };

  return (
    <>
      {!unmappedClients && <Spinner />}
      {unmappedClients && (
        <QuestionVariationForm
          formValues={surveyQuestionVariations ? surveyQuestionVariations : []}
          formFields={formFields}
          createNewRowFunc={createNewRowFunc}
          formLabel={formLabel}
          addRowLabel={addRowLabel}
          isDisabled={isReadOnly}
          header={header}
          unmappedClients={unmappedClients}
          postSurveyQuestionVariation={props.postSurveyQuestionVariation}
          showToast={props.showToast}
          updateSurveyQuestionVariation={props.updateSurveyQuestionVariation}
          deleteSurveyQuestionVariation={props.deleteSurveyQuestionVariation}
          getSurveyQuestions={props.getSurveyQuestions}
          surveyQuestionId={surveyQuestionId}
          updateClientMapping={props.updateClientMapping}
          taggedItems={taggedItems}
        />
      )}
    </>
  );
};

const mapStateToProps = state => {
  const {
    clients: { unmappedClients }
  } = state;
  return { unmappedClients };
};

export default compose(
  connect(mapStateToProps, {
    showToast,
    postSurveyQuestionVariation,
    updateSurveyQuestionVariation,
    deleteSurveyQuestionVariation,
    getSurveyQuestions,
    updateClientMapping
  })
)(SurveyQuestionSetupCard);
